import React, { useState } from 'react';
import { PartyAvatar } from '../../../components/shared';
import { leaveOrganization as leaveOrganizationRequest } from '../requests';
import { LeaveOrganizationModal } from './LeaveOrganizationModal';
import {
  StructuredList,
  StructuredListItem,
  Toggletip,
  Button,
  createToast,
  Toast,
} from 'components/core';
import { Text, Heading } from 'components/core/Typography';
import { Stack } from 'components/layout';

export type Membership = {
  id: string;
  organization: string;
  admin: boolean;
  hasReviewObligations: boolean;
};

const OrganizationsSection = (props: { memberships: Membership[] }) => {
  const [orgToLeave, setOrgToLeave] = useState('');
  const [membershipToDelete, setMembershipToDelete] = useState('');
  const [memberships, setMemberships] = useState<Membership[]>(
    props.memberships
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  const adminAction = () => {
    return (
      <Toggletip
        idPrefix="admin"
        triggerText="Unable to leave"
        triggerVariant="weak"
      >
        An admin is unable to leave the organization. To leave you must transfer
        ownership to another member.
      </Toggletip>
    );
  };

  const reviewAction = () => {
    return (
      <Toggletip
        idPrefix="admin"
        triggerText="Unable to leave"
        triggerVariant="weak"
      >
        If you want to leave this organization, you need to be removed from all
        reviews first.
      </Toggletip>
    );
  };

  const leaveAction = (membership: Membership) => {
    const showModal = () => {
      setOrgToLeave(membership.organization);
      setMembershipToDelete(membership.id);
      setIsModalOpen(true);
    };
    return (
      <Button size="sm" variant="outline" onClick={showModal}>
        Leave organization
      </Button>
    );
  };

  const removeMembership = () => {
    if (!membershipToDelete) {
      return;
    }
    leaveOrganizationRequest(membershipToDelete)
      .then(() => {
        setMemberships((memberships) => {
          return memberships.filter(
            (membership) => membership.id !== membershipToDelete
          );
        });
        setIsModalOpen(false);
        createToast(<Toast type="success">You have left {orgToLeave}</Toast>);
      })
      .catch(() => {
        createToast(
          <Toast type="danger">
            Error in leaving {orgToLeave}. Please try again.
          </Toast>
        );
        setIsModalOpen(false);
      });
  };

  const primaryAction = (membership: Membership) => {
    return membership.admin
      ? adminAction()
      : membership.hasReviewObligations
      ? reviewAction()
      : leaveAction(membership);
  };

  if (memberships.length === 0) {
    return null;
  }
  return (
    <>
      <LeaveOrganizationModal
        isOpen={isModalOpen}
        organizationName={orgToLeave}
        onClose={() => {
          setIsModalOpen(false);
        }}
        onRemove={removeMembership}
      />
      <Stack spacing={3}>
        <Heading type="h4">Your organizations</Heading>
        <StructuredList>
          {memberships.map((membership) => (
            <StructuredListItem
              icon={
                <PartyAvatar
                  name={membership.organization}
                  variant={'organization'}
                />
              }
              key={membership.id}
              primaryAction={primaryAction(membership)}
            >
              <Text>{membership.organization}</Text>
            </StructuredListItem>
          ))}
        </StructuredList>
      </Stack>
    </>
  );
};

export { OrganizationsSection };
